import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, API, fileUrl } from "@/lib/api";
import { CLP, UF, formatDateTime, formatDate, formatSessionRange } from "@/lib/format";
import { uploadImageFiles, IMAGE_ACCEPT, MAX_IMAGE_MB } from "@/lib/upload";
import { IncomeBreakdown } from "@/components/CostBreakdown";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";

const WIZARD = ["Datos", "Sesiones", "Imágenes", "Crear"];
const SESSION_WIZARD = ["Crear", "Cancelar"];
import { AddressesManager, AddressSelect } from "@/components/AddressesManager";
import { CopyWorkshopDialog } from "@/components/CopyWorkshopDialog";
import { OffersDialog } from "@/components/OffersDialog";
import { GroupsBar, EnrollmentModeField } from "@/components/WorkshopGroups";

const discountTag = (w) => {
  if (w.discount_mode === "permanent") {
    const d = (w.permanent_discounts || []).find((x) => x.id === w.permanent_active_id);
    return d ? ` (-${d.pct}%)` : "";
  }
  return w.offers?.length ? ` (${w.offers.length})` : "";
};
import { SessionCancelSetting } from "@/components/SessionCancelSetting";
import { TallerTypesSection } from "@/components/TallerTypesSection";
import { RescheduleSessionDialog } from "@/components/RescheduleSessionDialog";
import { PublicPreviewButton } from "@/components/PublicPreviewButton";
import { WizardSteps, WizardFooter } from "@/components/Wizard";
import { SessionsPreviewCalendar } from "@/components/SessionsPreviewCalendar";
import { MercadoPagoConnect } from "@/components/MercadoPagoConnect";

const SHOW_MP = false;
import { UpperNameField, CountedTextarea, BulletTextarea, DurationField, fromBulletText, WordCountTextarea, countWords, MAX_WORDS } from "@/components/WorkshopTextFields";
import { useWorkshopDraft, getDraft, saveDraft, clearDraft, flushDraft, fetchDraft } from "@/lib/workshopDraft";
import { MultiCategorySelect } from "@/components/MultiCategorySelect";
import { LocationPicker } from "@/components/LocationPicker";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { WorkshopPanel } from "@/components/WorkshopPanel";
import { WorkshopCardActions } from "@/components/WorkshopCardActions";
import { QuestionsInbox } from "@/components/QuestionsInbox";
import { MaestroResumen, dmy } from "@/components/MaestroResumen";
import { MessageCircleQuestion, Settings2, CalendarX2, ListChecks } from "lucide-react";
import { MaestroSettings } from "@/components/MaestroSettings";
import { DateField } from "@/components/DateField";
import { QrCheckin } from "@/components/QrCheckin";
import { SecondProfileCard } from "@/components/SecondProfileCard";
import { InstagramKit } from "@/components/InstagramKit";
import { CancelReminderDialog, shouldRemindCancel } from "@/components/CancelReminderDialog";
import { FichasWallet, FichasChip, PendingReminder, emitFichas, useFichas } from "@/components/FichasWallet";

import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { CHILEAN_BANKS, ACCOUNT_TYPES } from "@/lib/clRegions";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PaymentHistory } from "@/components/PaymentHistory";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { payPublication, checkoutNewWorkshop, PUB_RESULT } from "@/lib/publicationPay";
import { MaestroMobileNav, MAESTRO_TABS, DesktopMoreMenu } from "@/components/MaestroMobileNav";
import { PolicyText } from "@/components/PolicyText";
import { cachedGet } from "@/lib/attQueue";
import { MaestroHoy } from "@/components/MaestroHoy";
import { MaestroComunidad } from "@/components/MaestroComunidad";
import { clDay, clDate, clHM } from "@/lib/chileTime";
import {
  Plus, Loader2, QrCode, Wallet, Hammer, CalendarPlus, Store, FileText, Coins,
  TrendingUp, CheckCircle2, XCircle, Receipt, ImagePlus, X, Sparkles, Info, LayoutDashboard, UserRound, ExternalLink, Copy, CalendarClock, AlertTriangle, Tag, MapPin, ArrowRight, Sun,
} from "lucide-react";

const CATS = ["Cerámica", "Joyería", "Cocina", "Pintura", "Textil", "Madera", "Fotografía", "Floristería", "Idiomas", "Música", "Deportes"];
const PHONE_REQUIRED_MSG = "El teléfono móvil es obligatorio para coordinar las reservas vía WhatsApp";
const LEVELS = ["Principiante", "Intermedio", "Avanzado"];
const NO_LEVEL = "none";
const LevelItems = () => (
  <>
    <SelectItem value={NO_LEVEL} data-testid="level-option-none">No aplica</SelectItem>
    {LEVELS.map((l) => <SelectItem key={l} value={l} data-testid={`level-option-${l}`}>{l}</SelectItem>)}
  </>
);

export default function BusinessDashboard() {
  const { user, checkAuth } = useAuth();
  const [business, setBusiness] = useState(undefined);
  const [tab, setTabState] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return MAESTRO_TABS.some((x) => x.value === t) ? t : "resumen";
  });
  const setTab = useCallback((t) => {
    setTabState(t);
    const u = new URL(window.location.href);
    u.searchParams.set("tab", t);
    window.history.replaceState(window.history.state, "", u);
  }, []);
  const current = MAESTRO_TABS.find((t) => t.value === tab);

  const loadBiz = useCallback(async () => {
    const { data } = await cachedGet("/business/me");
    setBusiness(data);
  }, []);
  useEffect(() => { loadBiz(); }, [loadBiz]);
  const draft = useWorkshopDraft(!!business?.id);
  const [resumeSignal, setResumeSignal] = useState(0);
  const [openQs, setOpenQs] = useState(0);
  useEffect(() => {
    if (business?.id) api.get("/business/questions").then((r) => setOpenQs(r.data.filter((t) => t.status === "open").length)).catch(() => {});
  }, [business?.id]);

  if (business === undefined) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Cargando…</div>;
  if (!business) return <ProfileSetup onDone={() => { loadBiz(); checkAuth(); }} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary"><Store className="w-6 h-6" /></span>
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight truncate">{business.name}</h1>
          <p className="text-sm text-muted-foreground">{[business.commune, ...(business.categories && business.categories.length ? business.categories : [business.category])].filter(Boolean).join(" · ")}</p>
        </div>
        <FichasChip onClick={() => setTab("estado")} />
      </div>

      <PendingReminder onGo={() => setTab("talleres")} />

      {business.suspended_by_report && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm" data-testid="report-suspended-banner">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-destructive">Cuenta deshabilitada temporalmente</p>
            <p className="text-muted-foreground">Recibimos una denuncia relacionada con uno de tus talleres. Tus talleres no se muestran y sus sesiones están suspendidas hasta que VIVOTaller se contacte contigo para revisar el caso.</p>
          </div>
        </div>
      )}
      {business.can_publish === false && !business.suspended_by_report && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm" data-testid="publishing-suspended-banner">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-destructive">Publicaciones suspendidas</p>
            <p className="text-muted-foreground">Tu cuenta no tiene permisos activos para publicar talleres. Ponte en contacto con el administrador de VIVOTaller.</p>
          </div>
        </div>
      )}

      {draft && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="workshop-draft-banner">
          <Hammer className="w-5 h-5 text-primary shrink-0" />
          <span className="flex-1"><strong>Tienes un taller sin terminar{draft.f?.name ? `: «${draft.f.name.toUpperCase()}»` : ""}.</strong> Retómalo desde donde quedaste.</span>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { setTab("talleres"); setResumeSignal((n) => n + 1); }} data-testid="workshop-draft-resume">Retomar</Button>
            <Button size="sm" variant="ghost" onClick={() => clearDraft()} data-testid="workshop-draft-discard">Descartar</Button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {current && (
          <div className="lg:hidden flex items-center gap-2 mb-2 mt-2" data-testid="mobile-section-title">
            <current.icon className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">{current.label}</h2>
          </div>
        )}
        <div className="hidden lg:flex items-center justify-between gap-3">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="hoy" data-testid="tab-hoy"><Sun className="w-4 h-4 mr-1.5" />Hoy</TabsTrigger>
          <TabsTrigger value="resumen" data-testid="tab-resumen"><TrendingUp className="w-4 h-4 mr-1.5" />Resumen</TabsTrigger>
          <TabsTrigger value="panel" data-testid="tab-panel"><LayoutDashboard className="w-4 h-4 mr-1.5" />Panel</TabsTrigger>
          <TabsTrigger value="talleres" data-testid="tab-talleres"><Hammer className="w-4 h-4 mr-1.5" />Talleres</TabsTrigger>
          <TabsTrigger value="preguntas" data-testid="tab-preguntas"><MessageCircleQuestion className="w-4 h-4 mr-1.5" />Preguntas
            {openQs > 0 && <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center" data-testid="tab-preguntas-badge">{openQs}</span>}
          </TabsTrigger>
        </TabsList>
        <DesktopMoreMenu tabs={MAESTRO_TABS} main={DESKTOP_MAIN} value={tab} onChange={setTab} testId="desktop-more-menu" itemPrefix="tab-" />
        </div>

        <TabsContent value="hoy"><MaestroHoy onGoComunidad={() => setTab("comunidad")} /></TabsContent>
        <TabsContent value="comunidad"><MaestroComunidad /></TabsContent>
        <TabsContent value="resumen"><ResumenTab business={business} /></TabsContent>
        <TabsContent value="panel"><WorkshopPanel /></TabsContent>
        <TabsContent value="talleres"><TalleresTab business={business} onBizUpdated={loadBiz} resumeSignal={resumeSignal} /></TabsContent>
        <TabsContent value="qr"><QrTab /></TabsContent>
        <TabsContent value="preguntas"><QuestionsInbox onCount={setOpenQs} /></TabsContent>
        <TabsContent value="instagram"><InstagramKit /></TabsContent>
        <TabsContent value="estado"><div className="pt-6 space-y-6"><FichasWallet /><PaymentHistory /></div></TabsContent>
        <TabsContent value="politicas"><PoliticasTab /></TabsContent>
        <TabsContent value="presentacion">{SHOW_MP && <MercadoPagoConnect />}<PresentacionTab business={business} onSaved={loadBiz} /></TabsContent>
        <TabsContent value="direcciones"><AddressesManager onChanged={loadBiz} business={business} /></TabsContent>
        <TabsContent value="configuracion"><MaestroSettings /><CancelPolicyCard business={business} onSaved={loadBiz} /><SecondProfileCard /></TabsContent>
      </Tabs>
      <MaestroMobileNav value={tab} onChange={setTab} />
    </div>
  );
}

const DESKTOP_MAIN = ["hoy", "resumen", "panel", "talleres", "preguntas"];

function PresentacionTab({ business, onSaved }) {
  const initial = {
    avatar: business.avatar || "",
    profession: business.profession || "",
    years_experience: business.years_experience ?? "",
    bio: business.bio || "",
    gallery: business.gallery || [],
    birth_date: business.birth_date || "",
    show_age: !!business.show_age,
    categories: business.categories?.length ? business.categories : (business.category ? [business.category] : []),
  };
  const [f, setF] = useState(initial);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);
  const age = (() => {
    if (!f.birth_date) return null;
    const d = new Date(`${f.birth_date}T12:00:00`), t = new Date();
    return t.getFullYear() - d.getFullYear() - ((t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) ? 1 : 0);
  })();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setUploading(true);
    try { const [url] = await uploadImageFiles([file]); if (url) setF((p) => ({ ...p, avatar: url })); }
    finally { setUploading(false); }
  };
  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - (f.gallery?.length || 0);
    if (remaining <= 0) { toast.error("Máximo 6 fotos en la galería"); return; }
    setUploading(true);
    e.target.value = "";
    if (files.length > remaining) toast.info(`Solo se agregarán ${remaining} foto(s): máximo 6 en la galería`);
    try {
      const urls = await uploadImageFiles(files.slice(0, remaining));
      if (urls.length) setF((p) => ({ ...p, gallery: [...(p.gallery || []), ...urls] }));
    } finally { setUploading(false); }
  };
  const removeGallery = (i) => setF((p) => ({ ...p, gallery: p.gallery.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (f.bio !== (business.bio || "")) {
      const n = countWords(f.bio);
      if (n > MAX_WORDS) {
        toast.error(`Tu reseña profesional puede tener como máximo ${MAX_WORDS} palabras (tiene ${n}).`);
        document.querySelector('[data-testid="bio-input"]')?.focus();
        return;
      }
    }
    if (f.show_age && !f.birth_date) { toast.error("Ingresa tu fecha de nacimiento para mostrar tu edad"); return; }
    if (!f.categories.length) { toast.error("Marca al menos un tipo de taller"); return; }
    setLoading(true);
    try {
      await api.post("/business/profile", {
        ...business,
        avatar: f.avatar || null,
        profession: f.profession || null,
        years_experience: f.years_experience === "" ? null : Number(f.years_experience),
        bio: f.bio || null,
        gallery: f.gallery,
        birth_date: f.birth_date || null,
        show_age: f.show_age,
        categories: f.categories,
      });
      toast.success("Perfil de presentación guardado");
      onSaved?.();
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setLoading(false); }
  };

  return (
    <Card className="p-6 mt-6 space-y-5 max-w-2xl" data-testid="presentacion-tab">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-bold flex items-center gap-2"><UserRound className="w-5 h-5 text-primary" />Carta de Presentación Pública</h3>
        <a href={`/maestro/${business.id}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0" data-testid="view-public-profile">
          Ver perfil público <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-muted grid place-items-center shrink-0">
          {f.avatar ? <img src={fileUrl(f.avatar)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-8 h-8 text-muted-foreground" />}
        </div>
        <div>
          <Label>Fotografía de perfil / Avatar</Label>
          <label className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent/40 transition-colors" data-testid="avatar-upload-label">
            <ImagePlus className="w-4 h-4" /> Subir imagen
            <input type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={onAvatar} data-testid="avatar-upload" />
          </label>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP o GIF · máx {MAX_IMAGE_MB} MB</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Oficio o profesión principal</Label><Input value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })} placeholder="Ceramista & Escultora" data-testid="profession-input" /></div>
        <div><Label>Años de trayectoria</Label><Input type="number" min="0" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} placeholder="5" data-testid="years-input" /></div>
      </div>

      <div className="rounded-xl border border-border p-4 grid sm:grid-cols-2 gap-4 items-start" data-testid="age-section">
        <div>
          <Label>Fecha de nacimiento</Label>
          <DateField value={f.birth_date} min="1930-01-01" max={new Date(Date.now() - 16 * 365.25 * 864e5).toISOString().slice(0, 10)}
            onChange={(v) => setF({ ...f, birth_date: v })} testId="birth-date-input" floating yearNav />
          <p className="text-xs text-muted-foreground mt-1">Nunca se muestra tu fecha, solo tu edad si lo autorizas.</p>
        </div>
        <div className="flex items-start gap-3 sm:pt-6">
          <Switch checked={f.show_age} onCheckedChange={(v) => setF({ ...f, show_age: v })} data-testid="show-age-switch" />
          <div>
            <p className="text-sm font-medium">Mostrar mi edad en mi perfil público</p>
            <p className="text-xs text-muted-foreground" data-testid="age-preview">
              {f.show_age ? (age != null ? `Los alumnos verán: ${age} años` : "Ingresa tu fecha de nacimiento") : "Tu edad no se mostrará"}
            </p>
          </div>
        </div>
      </div>

      <TallerTypesSection selected={f.categories} onChange={(v) => setF({ ...f, categories: v })} />

      <WordCountTextarea label="Reseña profesional" value={f.bio} onChange={(v) => setF({ ...f, bio: v })} testId="bio-input"
        placeholder="Cuenta tu trayectoria, tu técnica, dónde aprendiste y qué vivirán tus alumnos en tus talleres… (hasta 300 palabras)" />

      <div>
        <Label>Galería del taller / espacio de trabajo <span className="text-muted-foreground font-normal">(hasta 6 fotos · máx {MAX_IMAGE_MB} MB c/u)</span></Label>
        <div className="grid grid-cols-3 gap-2 mt-2" data-testid="gallery-grid">
          {(f.gallery || []).map((src, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-muted group">
              <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGallery(i)} data-testid={`gallery-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-background/80 text-foreground opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {(f.gallery?.length || 0) < 6 && (
            <label className="aspect-square rounded-lg border-2 border-dashed border-border grid place-items-center cursor-pointer hover:border-primary/50 transition-colors" data-testid="gallery-upload-label">
              <input type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={onGallery} data-testid="gallery-upload" />
              <ImagePlus className="w-5 h-5 text-muted-foreground" />
            </label>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <PublicPreviewButton businessId={business.id} dirty={dirty} onSave={save} />
        <Button onClick={save} disabled={loading || uploading} data-testid="save-presentacion">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
        </Button>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-primary" />}
      </div>
      <p className="font-display font-extrabold text-2xl mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}

function ResumenTab() {
  return <MaestroResumen />;
}

function TalleresTab({ business, onBizUpdated, resumeSignal }) {
  const [workshops, setWorkshops] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sessionsFor, setSessionsFor] = useState(null);
  const [offersFor, setOffersFor] = useState(null);
  const [copyFrom, setCopyFrom] = useState(null);
  const [remind, setRemind] = useState(false);
  const afterCreate = () => { if (shouldRemindCancel(business)) setRemind(true); };
  const load = () => api.get("/business/workshops").then((r) => { setWorkshops(r.data); setLoaded(true); });
  useEffect(() => {
    load();
    const sp = new URLSearchParams(window.location.search);
    const res = PUB_RESULT[sp.get("pub")];
    if (res) {
      toast[res[0]](res[1]);
      if (res[0] === "success") afterCreate();
      sp.delete("pub");
      window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
      if (res[0] === "error") fetchDraft().then((d) => { if (d) onNew(); });
    }
  }, []); // eslint-disable-line
  const [paying, setPaying] = useState(null);
  const onPay = async (w) => {
    setPaying(w.id);
    try { await payPublication(w.id); toast.success("Taller publicado usando 1 Ficha. Agrega una fecha de sesión para que los alumnos lo vean."); await load(); afterCreate(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPaying(null); }
  };
  const [featuring, setFeaturing] = useState(null);
  const onFeature = async (w) => {
    if (!window.confirm(`¿Destacar "${w.name}" por 3 días en Explorar? Se usará 1 Ficha.`)) return;
    setFeaturing(w.id);
    try { await api.post(`/business/fichas/feature/${w.id}`); emitFichas(); toast.success("¡Listo! Tu taller aparece destacado en Explorar por 3 días."); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setFeaturing(null); }
  };

  const onNew = () => { setEditing(null); setOpen(true); };
  useEffect(() => { if (resumeSignal) onNew(); }, [resumeSignal]); // eslint-disable-line

  const onCopy = (w) => setCopyFrom(w);

  const dialogs = (
    <>
      <WorkshopDialog open={open} onOpenChange={setOpen} editing={editing} business={business} onSaved={(r) => { setOpen(false); load(); if (r?.created) afterCreate(); }} />
      {copyFrom && <CopyWorkshopDialog workshop={copyFrom} onClose={() => setCopyFrom(null)} onDone={() => { setCopyFrom(null); load(); afterCreate(); }} />}
      <CancelReminderDialog open={remind} hours={business?.policy_cancel_hours || 24} onClose={() => setRemind(false)} onSnoozed={onBizUpdated} />
      {sessionsFor && <SessionsDialog workshop={sessionsFor} onClose={() => { setSessionsFor(null); load(); }} />}
      {offersFor && <OffersDialog workshop={offersFor} onClose={() => { setOffersFor(null); load(); }} />}
    </>
  );

  if (loaded && workshops.length === 0) {
    const nombre = (business?.name || "").trim() || "Maestro";
    return (
      <div className="pt-6">
        <Card className="max-w-lg mx-auto text-center p-10 mt-6" data-testid="maestro-empty-state">
          <div className="mx-auto mb-5 grid place-items-center w-16 h-16 rounded-2xl bg-primary/10 text-primary">
            <Sparkles className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-extrabold tracking-tight" data-testid="empty-state-title">
            ¡Hola, {nombre}! Comencemos a crear tus talleres.
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">
            Comparte tus conocimientos y conecta con alumnos locales. Tu primer taller está a solo un clic.
          </p>
          <Button onClick={onNew} size="lg" className="mt-6" data-testid="create-first-workshop-btn">
            <Plus className="w-4 h-4 mr-1.5" />Crear mi primer Taller
          </Button>
        </Card>
        {dialogs}
      </div>
    );
  }

  return (
    <div className="pt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Mis Talleres</h2>
        <Button onClick={onNew} data-testid="new-workshop-btn">
          <Plus className="w-4 h-4 mr-1.5" />Nuevo taller
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {workshops.map((w) => (
          <Card key={w.id} className="p-4" data-testid={`biz-workshop-${w.id}`}>
            <div className="flex items-start justify-between gap-2">
              <Badge variant={w.active ? "default" : "outline"} data-testid={`workshop-status-${w.id}`}>{w.active ? "Activo" : "Inactivo"}</Badge>
              <WorkshopCardActions workshop={w} onEdit={() => { setEditing(w); setOpen(true); }}
                onToggle={async () => { await api.patch(`/business/workshops/${w.id}/toggle`); await load(); }} />
            </div>
            <div className="mt-2">
              <h3 className="font-bold">{w.name}</h3>
              <p className="text-xs text-muted-foreground">{[w.category, w.level].filter(Boolean).join(" · ")}</p>
            </div>
            {w.publication_status === "pending" && (
              <Badge variant="outline" className="mt-2 border-amber-400 text-amber-700 bg-amber-50" data-testid={`pending-payment-badge-${w.id}`}>Sin publicar</Badge>
            )}
            {w.publication_status !== "pending" && w.active && !w.upcoming_count && (
              <Badge variant="outline" className="mt-2 border-sky-400 text-sky-700 bg-sky-50" data-testid={`no-sessions-badge-${w.id}`}>No visible: agrega una sesión</Badge>
            )}
            <div className="text-xs text-muted-foreground mt-2 space-y-0.5" data-testid={`workshop-range-${w.id}`}>
              {w.created_at && (
                <p data-testid={`workshop-created-${w.id}`}>
                  Creado : {new Date(w.created_at).toLocaleDateString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric" }).replace(/-/g, "/")}
                </p>
              )}
              <p data-testid={`workshop-dates-${w.id}`}>
                {w.first_session_date
                  ? `Inicia : ${w.first_session_date.split("-").reverse().join("/")} | Termina : ${w.last_session_date.split("-").reverse().join("/")}`
                  : "Sin sesiones aún"}
              </p>
              <p className="font-medium text-foreground" data-testid={`workshop-mode-${w.id}`}>Modalidad {w.enrollment_mode === "free" ? "Sesiones sueltas" : "Grupal"}</p>
            </div>
            <p className="font-display font-bold text-lg mt-1">{CLP(w.price_clp)}</p>
            {w.publication_status === "pending" ? (
              <div className="mt-2 space-y-2" data-testid={`pending-payment-box-${w.id}`}>
                <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Usa 1 Ficha para publicar este taller. Mientras tanto no podrá darse a conocer.</span>
                </div>
                <Button size="sm" className="w-full" onClick={() => onPay(w)} disabled={paying === w.id} data-testid={`pay-commission-${w.id}`}>
                  {paying === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Coins className="w-4 h-4 mr-1.5" />Publicar usando 1 Ficha</>}
                </Button>
              </div>
            ) : w.upcoming_count > 0 ? (
              <p className="text-xs text-muted-foreground" data-testid={`workshop-upcoming-${w.id}`}>{w.upcoming_count} fecha{w.upcoming_count === 1 ? "" : "s"} próxima{w.upcoming_count === 1 ? "" : "s"} · Visible para alumnos</p>
            ) : (
              <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid={`not-published-warning-${w.id}`}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>No visible para los alumnos. Agrega una fecha de sesión para publicarlo.</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setSessionsFor(w)} data-testid={`sessions-btn-${w.id}`}>
                <CalendarPlus className="w-4 h-4 mr-1" />Sesiones
              </Button>
              <Button variant="outline" size="sm" onClick={() => onCopy(w)} data-testid={`copy-workshop-${w.id}`}>
                <Copy className="w-4 h-4 mr-1" />Copiar
              </Button>
              <span title={w.publication_status === "pending" ? "Paga la comisión para crear descuentos" : !w.upcoming_count ? "Primero crea una sesión para aplicar descuentos" : ""}>
                <Button variant="outline" size="sm" onClick={() => setOffersFor(w)} data-testid={`offers-btn-${w.id}`}
                  disabled={w.publication_status === "pending" || !w.upcoming_count}>
                  <Tag className="w-4 h-4 mr-1 text-coral" />Descuentos{discountTag(w)}
                </Button>
              </span>
              {w.publication_status !== "pending" && w.active && w.upcoming_count > 0 && (
                <Button variant="outline" size="sm" onClick={() => onFeature(w)} disabled={featuring === w.id} data-testid={`feature-btn-${w.id}`}
                  title="Aparece primero en Explorar por 3 días">
                  {featuring === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1 text-amber-500" />{w.featured_until && new Date(w.featured_until) > new Date() ? `Destacado hasta ${new Date(w.featured_until).toLocaleDateString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit" }).replace(/-/g, "/")} · +3 días` : "Destacar 3 días"} (1 ficha)</>}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
      {dialogs}
    </div>
  );
}

function WorkshopDialog({ open, onOpenChange, editing, business, onSaved }) {
  const bizCats = (business?.categories && business.categories.length) ? business.categories : (business?.category ? [business.category] : CATS);
  const empty = { name: "", description: "", category: bizCats[0], level: "Principiante",
    duration_min: 60, price_clp: 20000, capacity: 6, gift_enabled: true, includes: [], bring: [], images: [], address_id: null,
    enrollment_mode: "group", max_sessions: null,
    offer: { enabled: false, discount_type: "percent", discount_value: 10, starts_at: "", ends_at: "" } };
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pubFees, setPubFees] = useState(null);
  const fichas = useFichas();
  const [step, setStep] = useState(0);
  const [payNow, setPayNow] = useState(true);
  useEffect(() => {
    const d = !editing && open ? getDraft() : null;
    setF(editing ? { ...empty, ...editing } : d ? { ...empty, ...d.f } : empty);
    setStep(d?.step || 0); setPayNow(true);
    if (d) toast.info("Retomamos tu taller pendiente desde donde quedaste");
  }, [editing, open]); // eslint-disable-line
  useEffect(() => {
    if (!open || editing || !business?.id) return;
    const touched = f.name.trim() || (f.description || "").trim() || (f.images || []).length;
    if (touched) saveDraft(f, step);
  }, [f, step, open, editing, business?.id]);
  const nameMax = Math.max(150, (editing?.name || "").length);
  useEffect(() => { if (open) api.get("/business/fees").then((r) => setPubFees(r.data)).catch(() => {}); }, [open]);

  const uploadImages = async (fileList) => {
    const files = Array.from(fileList || []);
    const remaining = 3 - (f.images?.length || 0);
    if (remaining <= 0) { toast.error("Máximo 3 fotos por taller"); return; }
    setUploading(true);
    if (files.length > remaining) toast.info(`Solo se agregarán ${remaining} foto(s): máximo 3 por taller`);
    try {
      const urls = await uploadImageFiles(files.slice(0, remaining));
      if (urls.length) {
        setF((prev) => ({ ...prev, images: [...(prev.images || []), ...urls] }));
        toast.success(urls.length > 1 ? "Fotos subidas" : "Foto subida");
      }
    } finally { setUploading(false); }
  };
  const removeImage = (idx) => setF((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const next = () => {
    if (step === 0) {
      if (!f.name.trim()) { toast.error("Ingresa el nombre del taller"); return; }
      if (!f.price_clp) { toast.error("Ingresa el precio del taller"); return; }
      if (!f.capacity || f.capacity < 1) { toast.error("Ingresa los cupos del taller"); return; }
    }
    if (step === 1 && f.enrollment_mode === "free" && !f.max_sessions) { toast.error("Indica el máximo de sesiones por alumno"); return; }
    if (step === 2 && !(f.images || []).length) { toast.error("Sube al menos 1 foto del taller"); return; }
    setStep((s) => Math.min(3, s + 1));
    document.querySelector('[data-testid="workshop-dialog"]')?.scrollTo({ top: 0 });
  };

  const save = async () => {
    if (!f.name || !f.price_clp) { toast.error("Completa nombre y precio"); setStep(0); return; }
    if (!f.images || f.images.length === 0) { toast.error("Sube al menos 1 foto del taller"); setStep(2); return; }
    setLoading(true);
    try {
      const payload = {
        ...f, name: f.name.trim().toUpperCase(), price_clp: Number(f.price_clp), duration_min: Number(f.duration_min),
        includes: fromBulletText(f.includes),
        bring: typeof f.bring === "string" ? f.bring.split("\n").filter(Boolean) : f.bring,
        images: typeof f.images === "string" ? f.images.split("\n").filter(Boolean) : f.images,
      };
      payload.offer = f.offer?.enabled ? f.offer : { enabled: false };
      if (editing) { await api.put(`/business/workshops/${editing.id}`, payload); toast.success("Taller actualizado"); onSaved(); return; }
      if (payNow) {
        await flushDraft(f, 3);
        try {
          const r = await checkoutNewWorkshop(payload);
          if (r.created) { clearDraft(); toast.success(r.ficha_used ? "Taller publicado usando 1 Ficha. Agrega una fecha de sesión para que los alumnos lo vean." : "Taller creado sin costo gracias a tu descuento. Agrega una fecha de sesión para publicarlo."); onSaved({ created: true }); }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
          setLoading(false);
        }
        return;
      }
      const { data } = await api.post("/business/workshops", payload);
      clearDraft();
      if (data.publication_status === "pending") toast.success("Taller guardado sin publicar. Publícalo con 1 Ficha desde Mis Talleres.");
      else toast.success("Taller creado. Agrega una fecha de sesión para publicarlo.");
      onSaved({ created: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const noFichas = !!fichas && fichas.balance < 1;
  useEffect(() => { if (noFichas) setPayNow(false); }, [noFichas]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="workshop-dialog">
        <DialogHeader><DialogTitle data-testid="workshop-dialog-title">{editing ? "Editar taller" : "Nuevo taller"}</DialogTitle></DialogHeader>
        <WizardSteps steps={WIZARD} step={step} onGo={(i) => i < step && setStep(i)} />
        <div className={`space-y-3 ${step === 0 ? "" : "hidden"}`} data-testid="ws-step-1">
          <UpperNameField value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="ws-name" max={nameMax} />
          <CountedTextarea label="Descripción del Taller" value={f.description} onChange={(v) => setF({ ...f, description: v })} testId="ws-desc" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Categoría</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="ws-category"><SelectValue /></SelectTrigger>
                <SelectContent>{bizCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nivel</Label>
              <Select value={f.level || NO_LEVEL} onValueChange={(v) => setF({ ...f, level: v === NO_LEVEL ? "" : v })}>
                <SelectTrigger data-testid="ws-level"><SelectValue /></SelectTrigger>
                <SelectContent><LevelItems /></SelectContent>
              </Select>
            </div>
          </div>
          <AddressSelect value={f.address_id} onChange={(v) => setF((s) => ({ ...s, address_id: v }))} testId="ws-address" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Precio (CLP)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
                <Input inputMode="numeric" value={f.price_clp ? Number(f.price_clp).toLocaleString("es-CL") : ""}
                  onChange={(e) => setF({ ...f, price_clp: Number(e.target.value.replace(/\D/g, "")) || 0 })}
                  data-testid="ws-price" className="pl-7" placeholder="20.000" />
              </div>
            </div>
            <div><Label>Cupos <span className="text-muted-foreground font-normal">(por grupo)</span></Label>
              <Input type="number" min="1" value={f.capacity} onChange={(e) => setF({ ...f, capacity: Number(e.target.value) || 0 })} data-testid="ws-capacity" /></div>
          </div>
          <DurationField value={f.duration_min} onChange={(v) => setF({ ...f, duration_min: v })} testId="ws-duration" />
          <BulletTextarea label="¿Qué Incluye el Taller?" value={f.includes} onChange={(v) => setF({ ...f, includes: v })} testId="ws-includes" />
          <div className="flex items-center justify-between">
            <Label>Disponible para regalar</Label>
            <Switch checked={f.gift_enabled} onCheckedChange={(v) => setF({ ...f, gift_enabled: v })} />
          </div>
        </div>
        <div className={`space-y-3 ${step === 1 ? "" : "hidden"}`} data-testid="ws-step-2">
          <EnrollmentModeField f={f} setF={setF} />
        </div>
        <div className={`space-y-3 ${step === 2 ? "" : "hidden"}`} data-testid="ws-step-3">
          <div>
            <Label>Fotos del taller (1 a 3) <span className="text-destructive">*</span></Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {(f.images || []).map((src, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border group" data-testid={`image-preview-${i}`}>
                  <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} data-testid={`remove-image-${i}`}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {(f.images?.length || 0) < 3 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 grid place-items-center cursor-pointer hover:bg-primary/10 transition" data-testid="image-dropzone">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
                    <span className="flex flex-col items-center text-primary text-xs gap-1"><ImagePlus className="w-6 h-6" />Subir</span>
                  )}
                  <input type="file" accept={IMAGE_ACCEPT} multiple className="hidden" data-testid="image-input"
                    onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }} />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">Directo desde tu dispositivo · JPG, PNG, WEBP o GIF · máximo {MAX_IMAGE_MB} MB por foto (fotos HEIC de iPhone: expórtalas como JPG). Si subes varias, rotarán automáticamente para ser visualizadas por el alumno.</p>
          </div>
        </div>
        <div className={`space-y-3 ${step === 3 ? "" : "hidden"}`} data-testid="ws-step-4">
          <IncomeBreakdown fees={pubFees} balance={fichas?.balance} />
          {!editing && pubFees && pubFees.publication_fee_uf > 0 && (
            <div className="space-y-2">
              <label className={`flex items-start gap-2.5 rounded-xl border border-primary/30 bg-primary/5 p-3 ${noFichas ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`} data-testid="pay-now-label">
                <Checkbox checked={payNow} disabled={noFichas} onCheckedChange={(v) => setPayNow(!!v)} className="mt-0.5" data-testid="pay-now-checkbox" />
                <span className="text-sm font-medium">Publicar usando 1 Ficha{noFichas && <span className="block text-xs font-normal text-muted-foreground" data-testid="no-fichas-note">No tienes fichas disponibles. Compra un paquete en Billetera.</span>}</span>
              </label>
              {!payNow && (
                <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="pay-later-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Tu taller quedará guardado sin publicar. Podrás publicarlo después con 1 ficha desde “Mis Talleres”.</span>
                </div>
              )}
            </div>
          )}
        </div>
        <WizardFooter step={step} total={WIZARD.length} onBack={() => setStep((s) => s - 1)} onNext={next}
          onFinish={save} loading={loading} finishLabel={editing ? "Guardar cambios" : payNow && pubFees?.publication_fee_uf > 0 ? "Publicar usando 1 Ficha" : "Crear taller"} />
      </DialogContent>
    </Dialog>
  );
}

function SessionsDialog({ workshop, onClose }) {
  const [allSessions, setSessions] = useState([]);
  const [groups, setGroups] = useState(workshop.groups || []);
  const [activeGroup, setActiveGroup] = useState(workshop.groups?.[0]?.id);
  const sessions = groups.length ? allSessions.filter((s) => s.group_id === activeGroup) : allSessions;
  const groupCounts = allSessions.reduce((m, s) => (s.status !== "cancelled" ? { ...m, [s.group_id]: (m[s.group_id] || 0) + 1 } : m), {});
  useEffect(() => { workshop.groups = groups; }, [groups]); // eslint-disable-line
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("15");
  const [minute, setMinute] = useState("00");
  const [cap, setCap] = useState(8);
  const [repeat, setRepeat] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [editingSes, setEditingSes] = useState(null);
  const [hours, setHours] = useState(String(workshop.attendance_notice_hours || 6));
  const [savingHours, setSavingHours] = useState(false);
  const activeCount = allSessions.filter((s) => s.status !== "cancelled").length;
  const finish = async () => {
    if (Number(hours) === (workshop.attendance_notice_hours || 6)) { onClose(); return; }
    setSavingHours(true);
    try {
      await api.patch(`/business/workshops/${workshop.id}/attendance-hours`, { hours: Number(hours) });
      workshop.attendance_notice_hours = Number(hours);
      toast.success(`Cancelación sesión: ${hours} h antes de cada sesión`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingHours(false); }
  };

  const p2 = (n) => String(n).padStart(2, "0");
  const toDateInput = (d) => clDay(d);
  const minDate = toDateInput(new Date());
  const futureStarts = allSessions.filter((s) => s.status !== "cancelled" && new Date(s.starts_at) > new Date()).map((s) => new Date(s.starts_at));
  const firstStart = futureStarts.length ? new Date(Math.min(...futureStarts)) : null;
  const DAY = 24 * 60 * 60 * 1000;
  const maxDate = toDateInput(firstStart ? new Date(firstStart.getTime() + 60 * DAY) : new Date(Date.now() + 365 * DAY));

  const startDate = date ? clDate(date, hour, minute) : null;
  const endDate = startDate ? new Date(startDate.getTime() + (workshop.duration_min || 0) * 60000) : null;
  const fmtClock = (d) => clHM(d);
  const curTime = `${p2(hour)}:${p2(minute)}`;
  const totalNew = repeat ? Number(weeks) : 1;
  const pickDay = (d) => {
    const s = sessions.find((x) => x.status !== "cancelled" && toDateInput(new Date(x.starts_at)) === d);
    if (!s) { setDate(d); return; }
    const st = new Date(s.starts_at), en = new Date(st.getTime() + (workshop.duration_min || 0) * 60000);
    toast.info(`Este día ya tiene sesión (${fmtClock(st)} a ${fmtClock(en)}). Cancélala si quieres cambiar la hora.`);
  };

  const load = () => api.get(`/business/workshops/${workshop.id}/sessions`).then((r) => setSessions(r.data));
  useEffect(() => { load(); }, []);

  const [offerConflict, setOfferConflict] = useState(null);
  const add = async (adjust = false) => {
    if (!date) { toast.error("Elige un día en el calendario"); return; }
    if (startDate < new Date() || date > maxDate) {
      toast.error(`La fecha u hora de la sesión no es válida: debe ser futura y hasta el ${maxDate.split("-").reverse().join("-")}.`); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/business/workshops/${workshop.id}/sessions`, {
        starts_at: startDate.toISOString(), capacity: Number(cap), repeat_weeks: repeat ? Number(weeks) : 1, adjust_offers: adjust, group_id: activeGroup,
      });
      let msg = data.count === 1 ? "Sesión agregada" : `${data.count} sesiones agregadas`;
      if (data.busy_days > 0) msg += ` · ${data.busy_days} día${data.busy_days === 1 ? "" : "s"} omitido${data.busy_days === 1 ? "" : "s"} porque ya tenían sesión`;
      if (data.skipped > 0) msg += ` · ${data.skipped} omitidas (superan los 60 días desde la primera sesión)`;
      if (adjust) msg += " · periodos de descuento ajustados";
      setOfferConflict(null); setDate(""); setRepeat(false); toast.success(msg);
      if (!firstStart && data.count > 0) {
        const until = new Date(startDate.getTime() + 60 * DAY).toLocaleDateString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric" });
        toast.info(`Creaste la primera sesión del taller. Desde ahora tienes un periodo de 60 días para crear sesiones de este taller: hasta el ${until}.`, { duration: 12000 });
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "offers_conflict") setOfferConflict(d.msg);
      else toast.error(formatApiErrorDetail(d));
    }
    finally { setLoading(false); load(); }
  };
  const cancelSes = async (id) => {
    const { data } = await api.post(`/business/sessions/${id}/cancel`);
    toast.success(`Sesión cancelada. Se avisó a ${data.refunded_reservations} alumno(s); si ya te pagaron, coordina la devolución con ellos.`);
    load();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="sessions-dialog">
        <div className="shrink-0 px-6 pt-6 pb-3 space-y-3 border-b">
          <DialogHeader>
            <DialogTitle className="break-words pr-6" data-testid="sessions-dialog-title">{workshop.name}</DialogTitle>
            <DialogDescription data-testid="sessions-dialog-subtitle">Asignar sesiones</DialogDescription>
          </DialogHeader>
          <WizardSteps steps={SESSION_WIZARD} step={step} onGo={(i) => i < step && setStep(i)} testId="session-steps" tabTestId="session-step-tab" />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4" data-testid="sessions-dialog-body">
        {step === 1 ? (
          <SessionCancelSetting workshop={workshop} hasSessions={activeCount > 0} value={hours} onValueChange={setHours} />
        ) : (<>
        {groups.length > 0 && <GroupsBar workshopId={workshop.id} groups={groups} setGroups={setGroups} active={activeGroup} setActive={setActiveGroup} counts={groupCounts} />}
        <div className="rounded-xl border border-border p-3 space-y-3">
          <div>
            <Label>Elige el día</Label>
            <div className="mt-1.5">
              <SessionsPreviewCalendar sessions={sessions} date={date} times={[curTime]} repeat={repeat} weeks={weeks} min={minDate} max={maxDate} onPick={pickDay} />
            </div>
            {startDate ? (
              <p className="mt-2 rounded-lg bg-primary/10 text-primary text-sm font-medium px-3 py-2" data-testid="session-summary">
                Sesión: {formatSessionRange(startDate, workshop.duration_min || 0)}
              </p>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="session-summary-empty">Toca un día del calendario para empezar.</p>
            )}
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="session-horizon-limit">
              <CalendarClock className="w-3.5 h-3.5 text-primary inline -mt-0.5 mr-1" />
              Puedes agendar hasta el <strong className="text-foreground">{maxDate.split("-").reverse().join("-")}</strong>{firstStart ? " (60 días desde la primera sesión del taller)" : " (1 año desde hoy; al crear la primera sesión, el plazo será de 60 días desde esa fecha)"}.
            </p>
          </div>
          <div>
            <Label>Hora inicio</Label>
            <div className="flex items-center gap-1">
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger data-testid="session-hour"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 17 }, (_, i) => i + 6).map((h) => <SelectItem key={h} value={String(h)}>{p2(h)}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="font-bold">:</span>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger data-testid="session-minute"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["00", "15", "30", "45"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {endDate && (
            <p className="text-xs text-muted-foreground" data-testid="session-endtime">
              Dura {workshop.duration_min} min · Termina a las <span className="font-medium text-foreground">{fmtClock(endDate)}</span> hrs
            </p>
          )}
          {date && (
          <div className="flex items-center justify-between border-t border-border pt-3" data-testid="session-repeat-row">
            <Label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={repeat} onCheckedChange={setRepeat} data-testid="session-repeat-toggle" />
              Replicar este día cada semana
            </Label>
            {repeat && (
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">por</span>
                <Select value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
                  <SelectTrigger className="w-20" data-testid="session-weeks"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">sem.</span>
              </div>
            )}
          </div>
          )}
          {repeat && date && (
            <p className="text-xs text-muted-foreground" data-testid="session-repeat-hint">
              Se crearán hasta {totalNew} sesiones, una por semana, el mismo día y hora. Los días que ya tengan sesión se omiten.
            </p>
          )}
          {offerConflict && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 space-y-2" data-testid="offers-conflict-warning">
              <p className="flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />{offerConflict}</p>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setOfferConflict(null)} data-testid="offers-conflict-cancel">Cancelar</Button>
                <Button size="sm" onClick={() => add(true)} disabled={loading} data-testid="offers-conflict-confirm">Ajustar descuentos y agregar</Button>
              </div>
            </div>
          )}
          <Button onClick={() => add(false)} disabled={loading || !date} className="w-full" data-testid="add-session-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Agregar {totalNew === 1 ? "sesión" : `${totalNew} sesiones`}</>}
          </Button>
        </div>
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border p-3" data-testid={`session-row-${s.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium break-words" data-testid={`session-range-${s.id}`}>{formatSessionRange(s.starts_at, workshop.duration_min || 0)}</p>
                <p className="text-xs text-muted-foreground">{s.status === "cancelled" ? "Cancelada" : "Programada"}</p>
              </div>
              {s.status !== "cancelled" && new Date(s.starts_at) > new Date() && (
                s.has_students ? (
                  <TooltipProvider delayDuration={100}><Tooltip><TooltipTrigger asChild>
                    <button type="button" className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground touch-hit" data-testid={`reschedule-locked-${s.id}`}><Pencil className="w-3.5 h-3.5" /><Info className="w-3.5 h-3.5" /></button>
                  </TooltipTrigger><TooltipContent className="max-w-xs">Tiene alumnos inscritos: cancela la sesión para reprogramar.</TooltipContent></Tooltip></TooltipProvider>
                ) : (
                  <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setEditingSes(s)} data-testid={`reschedule-btn-${s.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Cambiar</Button>
                )
              )}
              {s.status !== "cancelled" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive shrink-0">Cancelar</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-md">
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Cancelar la sesión por fuerza mayor?</AlertDialogTitle>
                      <AlertDialogDescription>Se reembolsará el 100% a todos los inscritos y no se te cobrará la comisión de esta sesión.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Volver</AlertDialogCancel>
                      <AlertDialogAction onClick={() => cancelSes(s.id)} data-testid={`confirm-cancel-session-${s.id}`}>Sí, cancelar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))}
          {sessions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin sesiones aún.</p>}
        </div>
        {editingSes && <RescheduleSessionDialog session={editingSes} durationMin={workshop.duration_min || 0} minDate={minDate} maxDate={maxDate}
          onClose={(saved) => { setEditingSes(null); if (saved) load(); }} />}
        </>)}
        </div>
        <div className="shrink-0 px-6 py-3 bg-background border-t" data-testid="sessions-dialog-footer">
          <WizardFooter step={step} total={SESSION_WIZARD.length} onBack={() => setStep(0)} onNext={() => setStep(1)}
            nextDisabled={activeCount === 0} onFinish={finish} finishDisabled={activeCount === 0} loading={savingHours}
            finishLabel="Guardar y terminar" ids={{ back: "step-back-btn", next: "step-next-btn", finish: "notice-hours-save" }}
            hint={step === 0 && activeCount === 0 ? "Agrega al menos una sesión para continuar." : null} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QrTab() {
  return (
    <div className="pt-6 max-w-md mx-auto">
      <p className="text-sm text-muted-foreground mb-4 text-center">Escanea el QR de la sesión que trae el alumno para registrar su asistencia (Asiste QR).</p>
      <QrCheckin />
    </div>
  );
}

function InfoTip({ text }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Más información" data-testid="info-tip-trigger"
            className="touch-hit inline-grid place-items-center w-4 h-4 rounded-full text-primary align-middle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-foreground text-background leading-relaxed" data-testid="info-tip-content">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CancelPolicyCard({ business, onSaved }) {
  const [hours, setHours] = useState(String(business.policy_cancel_hours || 24));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/business/profile", { ...business, policy_cancel_hours: Number(hours) });
      toast.success("Cancelación anticipada actualizada"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 max-w-2xl space-y-4 mt-6" data-testid="cancel-policy-card">
      <h3 className="font-bold flex items-center gap-2" data-testid="cancel-policy-title"><CalendarX2 className="w-5 h-5 text-primary" />Cancelación anticipada</h3>
      <div>
        <Label className="flex items-center gap-1.5">
          Tiempo de antelación para cancelar taller
          <InfoTip text="Define con cuantas horas de anticipación debe cancelar el alumno para recibir la devolución del dinero que pagó por reservar un taller" />
        </Label>
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger data-testid="policy-hours"><SelectValue /></SelectTrigger>
            <SelectContent>{["2", "3", "6", "12", "24", "48", "72"].map((h) => <SelectItem key={h} value={h}>{h} horas</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1.5">Si el alumno cancela fuera de este plazo, el Maestro no se verá obligado a realizar la devolución del dinero.</p>
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-policies">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
      </Button>
    </Card>
  );
}

export function PoliticasTab() {
  return (
    <div className="pt-6 max-w-3xl">
      <Card className="p-6">
        <h3 className="font-bold mb-3 flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Políticas y Términos del Servicio</h3>
        <PolicyText />
      </Card>
    </div>
  );
}

function ProfileSetup({ onDone }) {
  const { user, logout } = useAuth();
  const [phoneErr, setPhoneErr] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  const [f, setF] = useState({ name: user?.name || "", categories: [],
    country: "Chile", region: "", commune: "", address: "", address_reference: "", lat: null, lng: null, phone: "",
    policy_cancel_hours: 24, planning_horizon_days: 60,
    card_last4: "4242" });
  const [editName, setEditName] = useState(false);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name) { toast.error("Ingresa tu nombre de Maestro / marca"); return; }
    if (!isValidPhone(f.phone)) { setPhoneErr(true); toast.error(PHONE_REQUIRED_MSG); document.querySelector('[data-testid="biz-phone"]')?.focus(); return; }
    if (f.categories.length === 0) { toast.error("Selecciona al menos un tipo de taller"); return; }
    if (!f.address_label?.trim()) { toast.error("Ponle un nombre a tu dirección (ej. Mi casa, Mi Taller, Cowork)"); return; }
    if (!f.region || !f.commune || !f.address) { toast.error("Completa la dirección predeterminada de tu taller (región, comuna y dirección)"); return; }
    if (f.lat == null || f.lng == null) { toast.error("Ubica tu dirección en el mapa con el botón “Ubicar en el mapa”"); return; }
    setLoading(true);
    try {
      await api.post("/business/profile", f); toast.success("¡Perfil listo! Ya puedes crear tu primer taller."); onDone();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-extrabold" data-testid="biz-setup-title">
        Configuración de Perfil - <span className="text-primary">{user?.name || f.name || "Maestro"}</span>
      </h1>
      <p className="text-muted-foreground mb-6">Completa estos datos para dejar tu perfil 100% operativo y empezar a publicar talleres.</p>
      <Card className="p-6 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <Label>Nombre de Maestro / marca</Label>
            {!editName && (
              <button type="button" onClick={() => setEditName(true)}
                className="text-xs font-medium text-primary hover:underline" data-testid="biz-name-edit">
                Editar nombre
              </button>
            )}
          </div>
          {editName ? (
            <Input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              onBlur={() => setEditName(false)} data-testid="biz-name" placeholder="ej. Arte Culinario" />
          ) : (
            <p className="mt-1 text-base font-semibold" data-testid="biz-name-display">{f.name || "—"}</p>
          )}
        </div>
        <div className={`rounded-xl p-3 -mx-1 ${phoneErr ? "border border-destructive bg-destructive/5" : ""}`}>
          <Label className="mb-1.5 block">1. Teléfono Móvil <span className="text-destructive">(obligatorio)</span></Label>
          <PhoneInput value={f.phone} onChange={(v) => { setF({ ...f, phone: v }); if (isValidPhone(v)) setPhoneErr(false); }} testId="biz-phone" />
          {phoneErr && <p className="text-xs text-destructive font-medium mt-1.5" data-testid="biz-phone-required">{PHONE_REQUIRED_MSG}</p>}
          <p className="text-xs text-muted-foreground mt-1.5">Tu número permitirá coordinar fácilmente con tus alumnos y recibir notificaciones de reservas vía WhatsApp.</p>
        </div>
        <div>
          <Label className="mb-2 block">2. Especialidades / Categorías <span className="text-muted-foreground font-normal">(elige una o varias)</span></Label>
          <MultiCategorySelect options={cats} selected={f.categories} onChange={(v) => setF({ ...f, categories: v })} allowCustom={false} />
        </div>

        <div className="border-t border-border pt-4">
          <p className="font-semibold text-sm mb-1">3. Dirección predeterminada de tu taller</p>
          <p className="text-xs text-muted-foreground mb-3">Aquí se dictarán tus talleres por defecto. Luego podrás agregar más espacios en la pestaña «Direcciones».</p>
          <div className="mb-3">
            <Label>Nombre de la dirección <span className="text-destructive">*</span></Label>
            <Input value={f.address_label || ""} maxLength={40} onChange={(e) => setF({ ...f, address_label: e.target.value.slice(0, 40) })}
              placeholder="ej. Mi casa, Mi Taller, Cowork" data-testid="biz-address-label" />
          </div>
          <LocationPicker
            value={{ region: f.region, commune: f.commune, address: f.address, address_reference: f.address_reference, lat: f.lat, lng: f.lng }}
            onChange={(v) => setF((s) => ({ ...s, ...v }))} />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" data-testid="biz-setup-cancel"
            onClick={async () => { await logout(); window.location.href = "/"; }}>Cancelar</Button>
          <Button onClick={save} disabled={loading} className="flex-1" data-testid="save-business">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar y Continuar"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Loader() { return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>; }
